import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Sun, Snowflake, Sprout, Wrench } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

const MODES = [
  {
    key: "active",
    label: "Aktiv sesong",
    sub: "utleie pågår",
    icon: Sun,
    // gull – matches brand
    chip: "bg-emerald-100 text-emerald-700 border-emerald-200",
    dot: "bg-emerald-500",
  },
  {
    key: "winter_prep",
    label: "Vinterklargjøring",
    sub: "batterier ned til 37 %, frostvæsker",
    icon: Wrench,
    chip: "bg-amber-100 text-amber-700 border-amber-200",
    dot: "bg-amber-500",
  },
  {
    key: "winter_storage",
    label: "Vinteropplag",
    sub: "batterier frakoblet, båt avslått",
    icon: Snowflake,
    chip: "bg-sky-100 text-sky-700 border-sky-200",
    dot: "bg-sky-400",
  },
  {
    key: "spring_prep",
    label: "Vårklargjøring",
    sub: "batterier tilkobles, storage 50 %",
    icon: Sprout,
    chip: "bg-amber-100 text-amber-700 border-amber-200",
    dot: "bg-amber-500",
  },
];

export default function SeasonModeCard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [mode, setMode] = useState(null);
  const [changedBy, setChangedBy] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/settings/season");
      setMode(r.data.mode);
      setChangedBy(r.data.changed_by_name);
    } catch { /* non-blocking */ }
  };
  useEffect(() => { load(); }, []);

  const setSeason = async (newMode) => {
    if (newMode === mode) return;
    const label = MODES.find((m) => m.key === newMode)?.label || newMode;
    if (!window.confirm(
      `Bytte sesongdrift til "${label}"?\n\n` +
      (newMode === "winter_prep"
        ? "Service og admin får varsel om at rutine for vinterklargjøring må startes."
        : newMode === "spring_prep"
          ? "Service og admin får varsel om at rutine for vårklargjøring må startes."
          : newMode === "winter_storage"
            ? "Alle båter forventes å ha batteriene frakoblet."
            : "Batteripolicy går tilbake til booking-basert."))) return;
    setSaving(true);
    try {
      await api.put("/settings/season", { mode: newMode });
      toast.success(`Sesongdrift satt til: ${label}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke endre");
    } finally {
      setSaving(false);
    }
  };

  if (mode === null) return null;
  const current = MODES.find((m) => m.key === mode) || MODES[0];
  const CurrentIcon = current.icon;

  return (
    <Card className="p-4" data-testid="season-mode-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-md ${current.chip}`}>
            <CurrentIcon className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#A57F39]">
              Sesongdrift
            </div>
            <div className="text-base font-semibold text-[#0C1824]" data-testid="season-mode-current">
              {current.label}
              <Badge className={`ml-2 border ${current.chip}`}>{current.sub}</Badge>
            </div>
            {changedBy && (
              <div className="text-[11px] text-[#2B3F4D] mt-0.5">
                Sist endret av {changedBy}
              </div>
            )}
          </div>
        </div>

        {isAdmin && (
          <div className="flex flex-wrap gap-1.5">
            {MODES.map((m) => {
              const Ic = m.icon;
              const active = m.key === mode;
              return (
                <Button
                  key={m.key}
                  size="sm"
                  variant={active ? "default" : "outline"}
                  onClick={() => setSeason(m.key)}
                  disabled={saving || active}
                  data-testid={`season-mode-${m.key}`}
                  className={active
                    ? `${current.chip} border font-semibold hover:${current.chip}`
                    : "border-slate-300 text-[#2B3F4D] hover:bg-slate-50"}
                >
                  <Ic className="w-3.5 h-3.5 mr-1" />
                  {m.label}
                </Button>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
}
